import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi, type MerchantDetail } from '../../api/adminApi';

const ALL_MODULE_KEYS = ['dashboard', 'accounting', 'sales', 'purchases', 'inventory', 'hr', 'projects', 'reports', 'settings'];

function formatDate(iso?: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const isActive = status?.toLowerCase() === 'active';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

export default function AdminMerchantView() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<MerchantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const numId = id ? parseInt(id, 10) : NaN;
    if (!id || isNaN(numId)) {
      setError('Invalid merchant');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    adminApi
      .getMerchantDetail(numId)
      .then((res) => setDetail(res.data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleToggleModule = async (moduleKey: string, currentlyEnabled: boolean) => {
    await applyModuleChanges([{ key: moduleKey, enable: !currentlyEnabled }]);
  };

  const applyModuleChanges = async (changes: { key: string; enable: boolean }[]) => {
    if (!detail || updatingKey || changes.length === 0) return;
    const numId = parseInt(id!, 10);
    if (isNaN(numId)) return;

    setUpdatingKey('bulk');
    try {
      const currentEnabled =
        detail.modules?.filter((m) => m.enabled).map((m) => m.key) ?? [...ALL_MODULE_KEYS];
      let newEnabled = [...currentEnabled];

      for (const { key, enable } of changes) {
        if (enable) {
          if (!newEnabled.includes(key)) newEnabled.push(key);
        } else {
          newEnabled = newEnabled.filter((k) => k !== key);
        }
      }

      if (newEnabled.length === ALL_MODULE_KEYS.length) {
        newEnabled = [];
      }

      const res = await adminApi.updateMerchant(numId, {
        enabled_modules: newEnabled.length === 0 ? [] : newEnabled,
      });
      setDetail(res.data);
      setSelectedKeys(new Set());
    } catch {
      // Keep current state on error
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleBulkDisable = () => {
    const toDisable = detail?.modules?.filter((m) => m.enabled && selectedKeys.has(m.key)).map((m) => m.key) ?? [];
    if (toDisable.length === 0) return;
    applyModuleChanges(toDisable.map((key) => ({ key, enable: false })));
  };

  const handleBulkEnable = () => {
    const toEnable = detail?.modules?.filter((m) => !m.enabled && selectedKeys.has(m.key)).map((m) => m.key) ?? [];
    if (toEnable.length === 0) return;
    applyModuleChanges(toEnable.map((key) => ({ key, enable: true })));
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllEnabled = () => {
    const enabled = detail?.modules?.filter((m) => m.enabled).map((m) => m.key) ?? [];
    setSelectedKeys((prev) => new Set(Array.from(prev).concat(enabled)));
  };

  const selectAllDisabled = () => {
    const disabled = detail?.modules?.filter((m) => !m.enabled).map((m) => m.key) ?? [];
    setSelectedKeys((prev) => new Set(Array.from(prev).concat(disabled)));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/admin/merchant"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Merchant list
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <span className="inline-block w-6 h-6 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin" />
            <p className="mt-2">Loading merchant…</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <p className="text-red-600 font-medium">{error}</p>
            <Link
              to="/admin/merchant"
              className="mt-2 inline-block text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Back to Merchant list
            </Link>
          </div>
        ) : detail ? (
          <div className="p-6 sm:p-8 space-y-8">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{detail.merchant_name || detail.name}</h1>
                <p className="text-slate-600 mt-0.5">{detail.first_name && detail.last_name ? `${detail.first_name} ${detail.last_name}` : detail.name}</p>
                <div className="mt-2">
                  <StatusBadge status={detail.account_status || 'active'} />
                </div>
              </div>
            </div>

            {/* Account information */}
            <div>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Account Information</h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <dt className="text-slate-500">Email</dt>
                  <dd className="font-medium text-slate-900">{detail.email}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Contact number</dt>
                  <dd className="text-slate-900">{detail.contact_number || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Location</dt>
                  <dd className="text-slate-900">{detail.location_address || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Registered</dt>
                  <dd className="text-slate-900">{formatDate(detail.created_at)}</dd>
                </div>
              </dl>
            </div>

            {/* Account status */}
            <div>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2">Account Status</h2>
              <StatusBadge status={detail.account_status || 'active'} />
            </div>

            {/* Modules */}
            <div>
              <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2">Modules</h2>
              <p className="text-sm text-slate-500 mb-3">
                Select modules, then use the buttons below to enable or disable multiple at once. You can also click a module to toggle it individually.
              </p>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={selectAllEnabled}
                  className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Select all enabled
                </button>
                <button
                  type="button"
                  onClick={selectAllDisabled}
                  className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Select all disabled
                </button>
                {selectedKeys.size > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={handleBulkDisable}
                      disabled={!!updatingKey}
                      className="text-xs px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      Disable selected ({selectedKeys.size})
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkEnable}
                      disabled={!!updatingKey}
                      className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      Enable selected ({selectedKeys.size})
                    </button>
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(detail.modules ?? []).map((m) => {
                  const isUpdating = updatingKey === m.key || (updatingKey === 'bulk' && selectedKeys.has(m.key));
                  const isSelected = selectedKeys.has(m.key);
                  return (
                    <div
                      key={m.key}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border transition-colors ${
                        m.enabled
                          ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                          : 'bg-slate-50 text-slate-500 border-slate-200'
                      } ${isSelected ? 'ring-2 ring-slate-400 ring-offset-1' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(m.key)}
                        disabled={!!updatingKey}
                        className="rounded border-slate-300 text-slate-700 focus:ring-slate-400"
                      />
                      <button
                        type="button"
                        onClick={() => handleToggleModule(m.key, m.enabled)}
                        disabled={!!updatingKey}
                        className="flex flex-1 items-center gap-2 text-left min-w-0"
                      >
                        {isUpdating ? (
                          <span className="w-4 h-4 shrink-0 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : m.enabled ? (
                          <svg className="h-4 w-4 shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                        <span className="font-medium truncate">{m.label}</span>
                        <span className="text-xs ml-auto shrink-0">{m.enabled ? 'Enabled' : 'Disabled'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
